import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Type, computed, inject } from '@angular/core';
import {
  ClaimsMapByPharmacy,
  ClaimsMapByPrescriber,
  ClaimsMapData,
  ClaimsMapFiltersType,
  ClaimsMapPharmacyFlag,
  ClaimsMapPrescriberFlag,
  HeatmapLayer,
} from '@app/admin/components/claims-map/claims-maps.types';
import { ToastService } from '@app/root/providers/toast-service';
import config from '@config';
import { OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { ClaimFlagSettingsSchema } from '@libs/shared/domain/claim/flag/claim-flag-settings.schema';
import { PROJECTION_INDEXES } from '@libs/shared/infrastructure/projection_indexes';
import { ThemeService } from '@services/theme.service';
import { UrlService } from '@services/url.service';
import { isDateRange } from '@shared/components/chitin/molecules/daterange-picker/date-range';
import { Filter } from '@shared/components/data-view/data-view-types';
import { OpenSearchClient } from '@shared/providers/opensearch-client';
import * as L from 'leaflet';
import { chain, cloneDeep, isArray, isEmpty, isString, sumBy } from 'lodash';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const MAX_LOCATION_TERMS_SIZE = 99999;
const MAX_FLAGS_TERMS_SIZE = 1000;

@Injectable({ providedIn: 'root' })
export class ClaimsMapService {
  private readonly http = inject(HttpClient);
  private readonly openSearchClient = inject(OpenSearchClient);
  private readonly toastService = inject(ToastService);
  private readonly urlService = inject(UrlService);
  private readonly themeService = inject(ThemeService);

  async loadHeatmapPlugin(): Promise<Type<HeatmapLayer>> {
    return (await import('heatmap.js/plugins/leaflet-heatmap/leaflet-heatmap.js' as any)).default;
  }

  static readonly OSM_LIGHT_URL = `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${config.cartoApiKey}`;
  static readonly OSM_DARK_URL = `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${config.cartoApiKey}`;
  static readonly OSM_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

  readonly initialTileUrl = computed(() =>
    this.themeService.theme() === 'dark' ? ClaimsMapService.OSM_DARK_URL : ClaimsMapService.OSM_LIGHT_URL,
  );

  leafletOptions = {
    layers: [L.tileLayer(this.initialTileUrl(), { maxZoom: 12, attribution: ClaimsMapService.OSM_ATTRIBUTION })],
    zoom: 5,
    center: L.latLng(35, -90),
    zoomControl: false,
  };

  applyTheme(map: L.Map, mode: 'light' | 'dark'): void {
    const url = mode === 'dark' ? ClaimsMapService.OSM_DARK_URL : ClaimsMapService.OSM_LIGHT_URL;
    map.eachLayer(layer => {
      if (layer instanceof L.TileLayer) {
        layer.setUrl(url);
      }
    });
  }

  getDays() {
    return [-3, -2, -1, 0, 1, 2, 3].map(day => moment().add(day, 'days').format('MM/DD/YYYY'));
  }

  getMarkerIcon() {
    const markerIconSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" height="28px" viewBox="0 0 384 512"><!--! Font Awesome Free 6.4.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path fill="#000000" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/></svg>';
    return L.divIcon({
      className: 'leafletMapMarker',
      html: L.Util.template(markerIconSvg, {}),
      iconAnchor: [10, 28],
      iconSize: [21, 28],
      popupAnchor: [-20, 20],
    });
  }

  getDefaultFlags(flags: ClaimFlagSettingsSchema[]) {
    const firstCategory = chain(flags).first().get('category').value();
    return flags.filter(flag => flag.category === firstCategory).map(flag => flag.name);
  }

  fetchByPharmacy(
    filter: OpenSearchRequestFilter[],
    playbook?: string,
  ): Observable<HttpErrorResponse | ClaimsMapData<ClaimsMapPharmacyFlag>> {
    filter = this.validateFilter(filter);

    const query = new URLSearchParams();
    if (playbook) query.set('playbook', playbook);

    return this.http
      .post<ClaimsMapByPharmacy>(`${this.urlService.AGGREGATES_URL}claims-flags/pharmacy?${query.toString()}`, {
        filter,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.toastService.showError("Couldn't load data.");
          return of(error);
        }),
        map(response => {
          if (response instanceof HttpErrorResponse) return response;
          return <ClaimsMapData<ClaimsMapPharmacyFlag>>{
            total: {
              count: response.total,
              sum: 0,
            },
            flags: response.aggregations.pharmacy.buckets.map(bucket => {
              const flagsCount = sumBy(bucket.flags.buckets, 'doc_count');

              return <ClaimsMapPharmacyFlag>{
                location: 'pharmacy',
                lat: bucket.info.hits.hits[0]._source.pharmacyLat,
                lng: bucket.info.hits.hits[0]._source.pharmacyLon,
                count: flagsCount, // number of claims for those flags
                claimCount: bucket.doc_count, // number of claims for those flags
                parameters: {
                  name: bucket.info.hits.hits[0]._source.pharmacyName,
                  ncpdp: bucket.key,
                  count: flagsCount,
                  claimCount: bucket.doc_count,
                  sum: bucket.ecoValue.value,
                  bucket: bucket,
                },
              };
            }),
          };
        }),
      );
  }

  fetchByPrescriber(filter: OpenSearchRequestFilter[]): Observable<HttpErrorResponse | ClaimsMapData<ClaimsMapPrescriberFlag>> {
    filter = this.validateFilter(filter);
    const flagsValue = filter.find(f => f.field === 'flagsList')?.value;
    const flags = isArray(flagsValue) ? (flagsValue as string[]) : [];

    return this.openSearchClient
      .aggs<ClaimsMapByPrescriber>(PROJECTION_INDEXES.PATIENT_ASSISTANCE_REQUEST_GENERAL_VIEW, {
        filter,
        aggs: {
          prescriber: {
            field: 'prescriberNpi',
            operator: 'terms',
            size: MAX_LOCATION_TERMS_SIZE,
            aggs: {
              name: { field: 'prescriberName', operator: 'terms', size: 1 },
              lat: { field: 'prescriberLat', operator: 'min' },
              lng: { field: 'prescriberLon', operator: 'min' },
              ecoValue: { field: 'flagsAmountsValue', operator: 'sum' },
              flags: {
                field: 'flagsList',
                operator: 'terms',
                include: flags.length ? flags : undefined,
                size: flags.length || MAX_FLAGS_TERMS_SIZE,
              },
            },
          },
        },
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.toastService.showError("Couldn't load data.");
          return of(error);
        }),
        map(response => {
          if (response instanceof HttpErrorResponse) return response;
          const buckets = response.prescriber.buckets;
          return <ClaimsMapData<ClaimsMapPrescriberFlag>>{
            total: { count: sumBy(buckets, 'count'), sum: 0 },
            flags: buckets.map(bucket => {
              const flagsCount = sumBy(bucket.flags.buckets, 'count');
              return <ClaimsMapPrescriberFlag>{
                location: 'prescriber',
                lat: bucket.lat.value,
                lng: bucket.lng.value,
                count: flagsCount,
                claimCount: bucket.count,
                parameters: {
                  name: bucket.name.buckets[0]?.key ?? bucket.key,
                  npi: bucket.key,
                  count: flagsCount,
                  claimCount: bucket.count,
                  sum: bucket.ecoValue.value,
                  bucket,
                },
              };
            }),
          };
        }),
      );
  }

  mapFilters(filters: Filter.AppliedDataViewFilter[]): ClaimsMapFiltersType {
    const dateRange = filters.find(filter => filter.field === 'created');
    if (dateRange) {
      dateRange.value = {
        from: (dateRange.value as { from: unknown }).from,
        to: (dateRange.value as { to: unknown }).to,
      };
    }

    const flagsField = filters.find(filter => filter.field === 'flagsList');
    let flags = (flagsField?.value as string[]) ?? [];
    if (!flags.length) {
      flags = flagsField?.options?.map(option => option.value) ?? [];
    }

    const productsField = filters.find(filter => filter.field === 'productNdc');
    let products = (productsField?.value as string[]) ?? [];
    if (!products.length) {
      products = productsField?.options?.map(option => option.value) ?? [];
    }

    const queryFilters: ClaimsMapFiltersType['filters'] = filters
      .filter(item => !isEmpty(item.value))
      .map(item => this.osFilterField(item));
    return {
      calculateBy: 'flagsCount',
      dateRange: dateRange?.value as any,
      flags,
      products,
      filters: queryFilters,
    };
  }

  private validateFilter(filter: OpenSearchRequestFilter[]): OpenSearchRequestFilter[] {
    const dateRangeFilters = filter.filter(f => f.field === 'created');
    if (dateRangeFilters.length < 1 || !('created' in dateRangeFilters[0].value)) {
      throw new Error('Date range is required');
    }

    const flagsFilter = filter.find(f => f.field === 'flagsList');
    return flagsFilter?.value ? filter : filter.filter(f => f.field !== 'flagsList');
  }

  private osFilterField(input: Filter.AppliedDataViewFilter): ClaimsMapFiltersType['filters'][0] {
    const item = cloneDeep(input);

    if (item.type === 'gt') item.value = { gt: item.value };
    if (item.type === 'lt') item.value = { lt: item.value };
    if (isDateRange(item.value)) {
      item.value = { gte: item.value.from.toISOString(), lte: item.value.to.toISOString() };
    }
    if (isArray(item.value)) {
      item.value = item.value.map(v => (isString(v) && v.includes('|') ? v.split('|')[1] : v));
    }

    const typesMap: Record<string, string> = {
      equal: 'match',
      contains: 'query_string',
      beginsWith: 'query_string',
      endsWith: 'query_string',
      isOneOf: 'terms',
      in: 'terms',
      dateRange: 'range',
      datetimeRange: 'range',
      gt: 'range',
      lt: 'range',
    };

    return {
      field: item.field,
      type: typesMap[item.type as string] ?? item.type,
      value: item.value,
    };
  }
}
